import React, { createContext, useContext, useEffect, useState } from 'react';
import { onAuthStateChanged, signInWithEmailAndPassword, signOut } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser]               = useState(null);
  const [profile, setProfile]         = useState(null);
  const [loading, setLoading]         = useState(true);   // true until auth + profile both resolved
  const [error, setError]             = useState(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        try {
          // Keep loading=true until profile is fetched
          const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
          if (userDoc.exists()) {
            setProfile({ uid: firebaseUser.uid, ...userDoc.data() });
          } else {
            setProfile({ uid: firebaseUser.uid, role: null });
          }
          setUser(firebaseUser);
        } catch (err) {
          console.error('Error loading user profile:', err);
          setError('Failed to load your profile. Please try again.');
          setUser(firebaseUser);
          setProfile({ uid: firebaseUser.uid, role: null });
        }
      } else {
        setUser(null);
        setProfile(null);
      }
      // Only mark loading done after BOTH auth state and profile are resolved
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  const login = async (email, password) => {
    setError(null);
    // Set loading true again so ProtectedRoute waits during the profile fetch
    setLoading(true);
    try {
      await signInWithEmailAndPassword(auth, email.toLowerCase().trim(), password);
      // onAuthStateChanged will fire and fetch profile, then set loading=false
    } catch (err) {
      setLoading(false);
      switch (err.code) {
        case 'auth/user-not-found':
        case 'auth/wrong-password':
        case 'auth/invalid-credential':
          throw new Error('Incorrect email or password.');
        case 'auth/too-many-requests':
          throw new Error('Too many attempts. Please wait a few minutes and try again.');
        default:
          throw new Error('Sign in failed. Please try again.');
      }
    }
  };

  const logout = () => {
    setProfile(null);
    setUser(null);
    return signOut(auth);
  };

  return (
    <AuthContext.Provider value={{ user, profile, loading, error, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
